import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Mail, Linkedin } from "lucide-react";

// The team — Andrew Regenberg is the only publicly confirmed name.
// Additional executives are placeholder slots ready for the user to fill in.
const TEAM = [
  {
    name: "Andrew Regenberg",
    role: "Vice President of Finance",
    bio: "Oversees RCCD's financial operations — capital strategy, equity transactions, and the underwriting that supports the firm's specific-plan and Executive Management work.",
    initials: "AR",
    confirmed: true,
    accent: "primary",
  },
  {
    name: "Executive Leadership",
    role: "President · Specific Plans",
    bio: "Leads RCCD's specific-plan authorship and entitlement strategy — the discipline behind 5 adopted plans and 2,200+ acres of approved framework.",
    initials: "PR",
    confirmed: false,
  },
  {
    name: "Executive Leadership",
    role: "Vice President · Operations",
    bio: "Manages day-to-day project delivery and the NMC consortium coordination across builders, agencies, and infrastructure programs.",
    initials: "VO",
    confirmed: false,
  },
  {
    name: "Executive Leadership",
    role: "Director · Entitlement",
    bio: "Leads agency relationships and public-hearing strategy across cities and counties throughout Southern California.",
    initials: "DE",
    confirmed: false,
  },
  {
    name: "Executive Leadership",
    role: "Director · Real Estate",
    bio: "Sources and structures land transactions — supporting the $350M+ in completed equity transactions and the pipeline ahead.",
    initials: "DR",
    confirmed: false,
  },
  {
    name: "Executive Leadership",
    role: "Project Manager",
    bio: "Carries individual specific plans from feasibility through council adoption — coordinating with civil, environmental, and design consultants.",
    initials: "PM",
    confirmed: false,
  },
];

export default function Team() {
  return (
    <>
      <PageHero
        eyebrow="Meet the Team · 05"
        title="The people"
        italic="behind RCCD."
        subtitle="A small, senior team — direct access from your first conversation through delivery. Decades of compounded experience in land entitlement, real estate, and executive management across Southern California."
        image="https://images.unsplash.com/photo-1497366216548-37526070297c"
      />

      {/* Team grid */}
      <section className="bg-paper py-20 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {TEAM.map((person, i) => (
              <motion.article
                key={person.name + i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className={`group relative border p-7 lg:p-10 transition-all flex flex-col bg-card ${
                  person.confirmed
                    ? 'border-border hover:border-clay shadow-soft hover:shadow-medium'
                    : 'border-dashed border-border/70 hover:border-clay/40'
                }`}
              >
                {/* Top-right index */}
                <span className="absolute top-5 right-5 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {String(i + 1).padStart(2, '0')}
                </span>

                {/* Avatar / placeholder */}
                <div className={`h-20 w-20 rounded-full flex items-center justify-center font-display text-2xl transition-transform group-hover:scale-105 ${
                  person.confirmed
                    ? 'bg-clay/15 text-clay ring-1 ring-clay/40'
                    : 'bg-secondary text-muted-foreground ring-1 ring-border'
                }`}>
                  {person.initials}
                </div>

                {/* Name + role */}
                <div className="mt-6">
                  <h3 className={`font-display text-2xl lg:text-3xl leading-tight tracking-[-0.01em] ${
                    person.confirmed ? 'text-ink' : 'text-ink/70'
                  }`}>
                    {person.name}
                  </h3>
                  <div className={`mt-1 mono text-[10px] uppercase tracking-[0.22em] ${
                    person.confirmed ? 'text-clay' : 'text-muted-foreground'
                  }`}>{person.role}</div>
                </div>

                <p className="mt-5 text-sm lg:text-base text-foreground/75 leading-relaxed flex-1">
                  {person.bio}
                </p>

                {person.confirmed ? (
                  <div className="mt-6 pt-5 border-t border-border flex items-center gap-4">
                    <a href="mailto:contact@rccdinc.com" className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-clay flex items-center gap-2 transition-colors">
                      <Mail className="h-3.5 w-3.5" /> Connect
                    </a>
                  </div>
                ) : (
                  <div className="mt-6 pt-5 border-t border-dashed border-border/60">
                    <span className="mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground/70">
                      Profile coming soon
                    </span>
                  </div>
                )}

                {/* Accent dot */}
                {person.confirmed && (
                  <span className="absolute -top-1 -left-1 h-2 w-2 rounded-full bg-clay" />
                )}
              </motion.article>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 lg:mt-24 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 pt-12 border-t border-border">
            <div className="max-w-2xl">
              <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay">Join us</span>
              <h3 className="font-display text-3xl lg:text-5xl text-ink mt-3 leading-tight tracking-[-0.02em]">
                Bring a project to RCCD.
              </h3>
              <p className="mt-4 text-foreground/70 max-w-md">
                A small, senior team that values direct access. Send us a parcel APN or a draft plan — you'll speak with someone on this page within one business day.
              </p>
            </div>
            <Button asChild className="h-12 rounded-none bg-ink text-paper hover:bg-clay mono text-[11px] uppercase tracking-[0.22em] px-6">
              <Link to="/contact-us">
                Start a conversation <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
